import { Component } from "../base/Component";
import { ComponentFactory } from "../../service/ComponentFactory";
import { PageContext } from "../../service/PageContext";

type RelatedItem = {
    title: string;
    url: string;
    posteddate: string;
    engagementtime: number;
    video?: string;
    authors?: string;
    guestname?: string;
    thumb?: string;
};

export class PodcastSavedYouMayAlsoLikeDiscoverability extends Component {
    private static healioUrl = "https://www.healio.com";
    private seedEpisodeIds: string[];
    private seedSeriesIds: string[];
    private savedIds: Set<string>;
    private hwsHost: string | undefined;
    private loaded: boolean;

    constructor(element: HTMLElement, context: PageContext) {
        super(element, context);
        this.seedEpisodeIds = this.parseIds(this._element.dataset.seedEpisodeIds);
        this.seedSeriesIds = this.parseIds(this._element.dataset.seedSeriesIds);
        this.savedIds = new Set(this.parseIds(this._element.dataset.savedIds));
        this.hwsHost = this._element.dataset.hwsHost;
        this.loaded = false;
    }

    public async init(): Promise<void> {
        const component = this;
        $(async () => {
            if (window.Visitor !== undefined && window.Visitor !== "") {
                component.loadRecommendations();
            } else {
                $(window).one("globalsloaded", () => component.loadRecommendations());
                window.setTimeout(() => component.loadRecommendations(), 1500);
            }
        });
    }

    private parseIds(value: string | undefined): string[] {
        if (!value) {
            return [];
        }
        return value
            .split(",")
            .map((x) => x.trim().toLowerCase())
            .filter((x) => x.length > 0);
    }

    private normalizeUrl(url: string | undefined): string {
        if (!url) {
            return "";
        }
        return url.toString().replace(PodcastSavedYouMayAlsoLikeDiscoverability.healioUrl, "").trim().toLowerCase();
    }

    private extractCleanIdFromUrl(url: string | undefined): string {
        const normalized = this.normalizeUrl(url);
        const match = normalized.match(/\/find\/([0-9a-f-]{32,36})/i);
        if (!match || !match[1]) {
            return "";
        }
        return match[1].replace(/[^0-9a-f]/gi, "").toLowerCase();
    }

    private getThumbnailUrl(item: RelatedItem): string {
        const thumb = item?.thumb;
        if (!thumb) return "";
        const mediaidindex = thumb.indexOf("mediaid");
        if (mediaidindex > -1) {
            let thumbnailid = thumb.substr(mediaidindex, mediaidindex + 41);
            thumbnailid = thumbnailid.replace('mediaid="{', "").replace('}"', "").replace('"', "").replace(/-/g, "");
            return thumbnailid.length > 1 ? "/~/media/" + thumbnailid + ".png?w=400" : "";
        }
        if (thumb.startsWith("http://") || thumb.startsWith("https://") || thumb.startsWith("/")) return thumb;
        return "";
    }

    private populateCredits(cardElem: HTMLElement, item: RelatedItem): void {
        const credits = cardElem.querySelector(".podcast-episode-card__credits") as HTMLElement;
        const hostItem = cardElem.querySelector(".podcast-episode-card__credit-item--host") as HTMLElement;
        const guestItem = cardElem.querySelector(".podcast-episode-card__credit-item--guest") as HTMLElement;
        const hostValue = hostItem?.querySelector(".value") as HTMLElement;
        const guestValue = guestItem?.querySelector(".value") as HTMLElement;

        let hostText = "";
        let guestText = "";
        const authorsRaw = (item?.authors || "").trim();
        if (authorsRaw) {
            const splitAuthors = authorsRaw.split("|");
            hostText = (splitAuthors[0] || "").trim();
            guestText = (splitAuthors[1] || "").trim();
        }
        if (!guestText && item?.guestname) guestText = item.guestname;

        if (hostItem && hostValue) {
            if (hostText) {
                hostValue.innerHTML = hostText;
                hostItem.classList.remove("d-none");
            } else {
                hostItem.classList.add("d-none");
            }
        }
        if (guestItem && guestValue) {
            if (guestText) {
                guestValue.innerHTML = guestText;
                guestItem.classList.remove("d-none");
            } else {
                guestItem.classList.add("d-none");
            }
        }
        if (credits) {
            if (hostText || guestText) credits.classList.remove("d-none");
            else credits.classList.add("d-none");
        }
    }

    private fetchRelatedForSeed(seedId: string): Promise<RelatedItem[]> {
        const component = this;
        const hostPrefix = component.hwsHost || "";
        return new Promise((resolve) => {
            $.ajax({
                type: "GET",
                url: hostPrefix + "/~/hws/relatedchips/" + seedId + "/podcast/na/na",
                success: function (msg: any): void {
                    resolve(msg?.response?.relatedItems || []);
                },
                error: function (): void {
                    resolve([]);
                },
                timeout: 5000
            });
        });
    }

    private async loadRecommendations(): Promise<void> {
        if (this.loaded) return;
        this.loaded = true;

        if (window.Visitor && (window.Visitor.isbot || window.Visitor.type === "DC")) return;

        const seeds = [...this.seedEpisodeIds, ...this.seedSeriesIds];
        if (!seeds.length) {
            return;
        }

        const selected: RelatedItem[] = [];
        const seenUrls = new Set<string>();

        for (const seed of seeds) {
            if (selected.length >= 2) {
                break;
            }
            const relatedItems = await this.fetchRelatedForSeed(seed);
            for (const item of relatedItems) {
                if (selected.length >= 2) {
                    break;
                }
                const normalizedUrl = this.normalizeUrl(item.url);
                if (!normalizedUrl || seenUrls.has(normalizedUrl)) {
                    continue;
                }
                const cleanId = this.extractCleanIdFromUrl(item.url);
                if (cleanId && this.savedIds.has(cleanId)) {
                    continue;
                }
                seenUrls.add(normalizedUrl);
                selected.push(item);
            }
        }

        if (!selected.length) {
            return;
        }

        selected.forEach((item, idx) => {
            const cardElem = document.getElementById("savedYmalCard" + (idx + 1));
            if (!cardElem) return;

            const postedDate = new Date(item.posteddate);
            const formattedPostedDate = postedDate.toLocaleString("en-US", { day: "numeric", month: "long", year: "numeric" });
            (cardElem.querySelector(".podcast-episode-card__date") as HTMLElement).innerHTML = formattedPostedDate;
            (cardElem.querySelector(".podcast-episode-card__title") as HTMLElement).innerHTML = item.title;
            (cardElem.querySelector(".podcast-episode-card__duration") as HTMLElement).innerHTML = (item.engagementtime || 0) + " min";
            const link = item.url.toString().replace(PodcastSavedYouMayAlsoLikeDiscoverability.healioUrl, "");
            cardElem.setAttribute("href", link);

            const saveElem = cardElem.querySelector(".save.save--inline") as HTMLElement;
            if (saveElem) {
                saveElem.setAttribute("data-activity-id", item.url.replace("/find/", "").replace(/[{}]/g, ""));
                saveElem.setAttribute("data-activity-title", item.title || "");
                saveElem.setAttribute("data-activity-url", link);
            }

            const imgElem = cardElem.querySelector(".podcast-episode-card__media img") as HTMLImageElement;
            const imageSrc = this.getThumbnailUrl(item);
            if (imgElem) {
                if (imageSrc) {
                    imgElem.src = imageSrc;
                    imgElem.alt = item.title || "";
                    imgElem.classList.remove("d-none");
                } else {
                    imgElem.classList.add("d-none");
                }
            }

            this.populateCredits(cardElem, item);

            const videoElem = cardElem.querySelector(".podcast-episode-card__video") as HTMLElement;
            if (videoElem) {
                if (item.video && item.video !== "") videoElem.classList.remove("d-none");
                else videoElem.classList.add("d-none");
            }

            cardElem.classList.remove("d-none");
        });

        document.getElementById("podcastSavedYouMayAlsoLike")?.classList.remove("d-none");
    }
}

ComponentFactory.registerComponent("PodcastSavedYouMayAlsoLikeDiscoverability", PodcastSavedYouMayAlsoLikeDiscoverability);
